import type { AssignedPermissions } from '../@types/AssignedPermissions';
import type { Actions } from '../actions';
import {
  ASSIGNED_PERMISSIONS_SET_SHOULD_SHOW_MODAL,
  RCV_ASSIGNED_PERMISSIONS_GET,
  RCV_ASSIGNED_PERMISSIONS_REMOVE,
  RCV_ASSIGNED_PERMISSIONS_SET,
  REQ_ASSIGNED_PERMISSIONS_GET,
  REQ_ASSIGNED_PERMISSIONS_REMOVE,
  REQ_ASSIGNED_PERMISSIONS_SET,
  REQUEST_FAILED,
} from '../actions/assigned-permissions';
import { requestStatus, responseStatus } from '../shared/statuses';

/* ----------------- INITIAL STATE ----------------- */

interface AssignedPermissionsState {
  requestStatus: number;
  responseStatus: number;
  previousRequest: number | null;
  assignedPermissions: AssignedPermissions | null;
  lastLookupEntityId: string | null;
  shouldShowModal: boolean;
}

const _initialState: AssignedPermissionsState = {
  requestStatus: requestStatus.WAITING,
  responseStatus: responseStatus.OK,
  previousRequest: null,
  assignedPermissions: null,
  lastLookupEntityId: null,
  shouldShowModal: false,
};

/* ----------------- STATUS HELPERS ----------------- */

const getSuccessfulResponse = (newState: AssignedPermissionsState) => {
  return {
    ...newState,
    requestStatus: requestStatus.WAITING,
    responseStatus: responseStatus.OK,
  };
};

const getPendingRequest = (state: AssignedPermissionsState, status: number) => {
  return {
    ...state,
    requestStatus: status,
    responseStatus: responseStatus.PENDING,
  };
};

/* ----------------- REDUCER ----------------- */

const assignedPermissionsReducer = (
  state: AssignedPermissionsState = _initialState,
  action: Actions,
): AssignedPermissionsState => {
  switch (action.type) {
    case ASSIGNED_PERMISSIONS_SET_SHOULD_SHOW_MODAL:
      return { ...state, shouldShowModal: action.value };

    case REQ_ASSIGNED_PERMISSIONS_GET:
      return getPendingRequest(
        { ...state, lastLookupEntityId: action.entityId, assignedPermissions: null },
        requestStatus.FETCHING,
      );

    case REQ_ASSIGNED_PERMISSIONS_SET:
      return getPendingRequest(state, requestStatus.CREATING);

    case REQ_ASSIGNED_PERMISSIONS_REMOVE:
      return getPendingRequest(state, requestStatus.REMOVING);

    case RCV_ASSIGNED_PERMISSIONS_GET:
      return getSuccessfulResponse({ ...state, assignedPermissions: action.response });

    case RCV_ASSIGNED_PERMISSIONS_SET:
      return getSuccessfulResponse({
        ...state,
        assignedPermissions: action.response,
        shouldShowModal: false,
      });

    case RCV_ASSIGNED_PERMISSIONS_REMOVE:
      return getSuccessfulResponse({ ...state, assignedPermissions: null, shouldShowModal: false });

    case REQUEST_FAILED:
      return {
        ...state,
        requestStatus: requestStatus.WAITING,
        responseStatus: responseStatus.FAILED,
        previousRequest: state.requestStatus,
      };

    default:
      return state;
  }
};

export default assignedPermissionsReducer;
